import { Component, ComponentChildren } from 'preact';
import { Builder } from './builder.js';
import { Icon } from '../icons.js';
import { LogoUploadButton } from './upload.js';
import { DesktopPart, getPartTitle, PartConfig } from './part.js';
import { Maybe } from '../../../common/data.js';
import { getUploadPreviewURL } from '../../lib/images.js';
import { ensure, findMatch, isSome, maybe, notEmpty } from '../../../common/util.js';
import { showAlert } from '../popup.js';
import { DesignPart, TextPartType } from '../../../common/data/designs.js';
import { FlowDecorationType, FullPublicProduct } from '../../../common/data/products.js';
import { PublicFlow } from '../../../common/data/flows.js';
import { PublicDomain } from '../../../common/data/domains.js';
import { classNames, getEventValue } from '../../lib/util.js';

export interface BuilderSettingsProps {
	product: FullPublicProduct;
	flow: PublicFlow;
	builder: Builder;
	parts: DesignPart[];
	copiedColor?: number[];
	selected?: Maybe<string>;
	domain: PublicDomain;
}

interface BuilderSettingsState {
	uploadError?: string;
}
interface BuilderMobileSettingsState extends BuilderSettingsState {
	popupPart?: DesignPart;
	popupColor?: number;
}
interface BuilderDesktopSettingsState extends BuilderSettingsState {
	/**
	 * false = nothing open
	 * null = default (open the apparel)
	 */
	openPart: number | string | false | null;
}

export function BuilderSettingsBlock({ open, toggle, icon, title, previews, body }: {
	open: boolean;
	toggle: () => void;
	icon: ComponentChildren;
	title: ComponentChildren;
	previews?: Maybe<() => ComponentChildren>;
	body: () => ComponentChildren;
}) {
	return <div className="builder-settings__block">
		<div className="builder-settings__block-header" onClick={toggle}>
			{icon}
			<div className="builder-settings__block-header-title">{title}</div>
			{!open && previews && <div className="builder-settings__block-header-previews">{previews()}</div>}
			{open ? <Icon name="angle-down" /> : <Icon name="angle-up" />}
		</div>
		{open && <>{body()}</>}
	</div>;
}

export function BuilderProductSettings({ product, builder, apparel }: {
	product: FullPublicProduct,
	builder: Builder;
	apparel: Maybe<DesignPart>;
}) {
	const { fabrics, flow } = builder.props;
	const guides: Partial<Record<FlowDecorationType, string>> = {
		dtf: 'direct-to-film',
		'cut-and-sew': 'sublimation',
		'screen-print': 'screen-printing',
		embroidery: 'embroidery',
	};
	const curFabric = apparel?.fabric ?? fabrics?.[0]?.id;
	return <div className="builder-settings__product">
		{product.meta_description && <p class="builder-settings__description">
			{product.meta_description}
		</p>}
		<div className="builder-settings__product-links">
			<a target="_blank" href={`/product/${product.alias ?? product.id}`} rel="noopener" >
				<Icon name="pop-out" />
				<span>product info</span>
			</a>{' '}
			{maybe(flow.decoration && guides[flow.decoration], g => <a target="_blank" href={`/guide/fabric/${g}`} rel="noopener">
				<Icon name="pop-out" />
				<span>fabric guide</span>
			</a>)}
		</div>
		{apparel && notEmpty(fabrics) && <>
			<label for="fabric-dd">Fabric:</label>
			<select
				id="fabric-dd"
				value={curFabric}
				onChange={e => {
					builder.manager.updatePart(apparel.id, { fabric: getEventValue(e) });
				}}
			>
				{fabrics.map(fab => <option
					key={fab.id}
					value={fab.id}
				>{fab.name}</option>)}
			</select>
			{maybe(
				fabrics.find(f => f.id == curFabric)?.description,
				desc => <p class="builder-settings__description">{desc}</p>
			)}
		</>}
	</div>;
}

export class BuilderDesktopSettings extends Component<BuilderSettingsProps, BuilderDesktopSettingsState> {

	constructor(props: BuilderSettingsProps) {
		super(props);
		this.state = {
			openPart: props.selected ?? null,
		};
	}

	render({ product, flow, builder, parts, selected, domain }: BuilderSettingsProps, { openPart, uploadError }: BuilderDesktopSettingsState) {

		const { maxOneLogo, roster, nameNumber } = ensure(product.features);
		const canAddLogo = !maxOneLogo || !parts.find(it => it.type == 'logo' || it.type == 'text');
		const apparel = findMatch(parts, { type: 'apparel' });

		const showPart = (part: DesignPart, open: boolean) => <DesktopPart
			key={part.id}
			part={part}
			builder={builder}
			open={open}
			removable={['logo', 'text', 'name', 'number', 'alias'].includes(part.type)}
			toggle={() => {
				if (builder.state.selectedPart === part.id) {
					builder.selectPart(undefined);
					this.setState({ openPart: false });
				}
				else {
					builder.selectPart(part.id);
				}
			}}
			domain={domain}
		/>;

		return <div className="builder-settings">
			<div className="builder-settings__inner">
				
				<BuilderSettingsBlock
					open={openPart == 'product' || openPart === null}
					toggle={() => this.toggleOpenPart('product')}
					icon={<Icon name="apparel" />}
					title={<h1>{product.name}</h1>}
					body={() => <BuilderProductSettings
						builder={builder}
						product={product}
						apparel={apparel}
					/>}
				/>
				
				{maybe(apparel, part => showPart(part, openPart == part.id))}

				{canAddLogo && <BuilderSettingsBlock
					open={openPart == 'addLogo'}
					toggle={() => this.toggleOpenPart('addLogo')}
					icon={<Icon name="plus" />}
					title="Add Logo"
					body={() => <div className="builder-settings__add-logo">
						<div className="builder-settings__add-logo-buttons">
							<LogoUploadButton
								builder={builder}
								setError={e => this.setState({ uploadError: e })}
								title="Upload"
							/>
							{builder.props.haveUser && <button class="builder-settings__browse-artwork" onClick={() => builder.browseArtwork()}>
								<Icon name="image-file" />
								<span>My Logos</span>
							</button>}
						</div>
						{uploadError && <p className="builder-settings__add-logo-error">
							{uploadError}
						</p>}
						{product && <div>
							<p>
								<strong>EPS, AI or SVG</strong> vector files are supported.
								{' '}<a href="/faq">Learn More</a>
							</p>
							<p>
								If you upload <strong>PNG or JPEG</strong> files these will be manually converted to vector for an extra <strong>$15</strong> fee.
							</p>
						</div>}
						{this.props.product.id == 'mask' && <p style={{ color: 'maroon' }}>
							<strong>Warning</strong>: Please don't place logos on the center seam as they may be distorted.
						</p>}
					</div>}
				/>}

				{canAddLogo && <BuilderSettingsBlock
					open={openPart == 'addText'}
					toggle={() => this.toggleOpenPart('addText')}
					icon={<Icon name="plus" />}
					title="Add Text"
					body={() => <div className="builder-settings__add-logo">
						<div className="builder-settings__add-logo-buttons">
							{roster && nameNumber && <button class="builder-settings__add-text" onClick={this.addTextPart('name')}>
								<Icon name="user" />
								<span>Add Name</span>
							</button>}
							{roster && nameNumber && <button class="builder-settings__add-text" onClick={this.addTextPart('number')}>
								<Icon name="one" />
								<span>Add Number</span>
							</button>}
							<button class="builder-settings__add-text" onClick={this.addTextPart()}>
								<Icon name="text" />
								<span>Add Text</span>
							</button>
							{roster && nameNumber && <button className="builder-settings__add-text" onClick={this.addTextPart('alias')}>
								<Icon name="alias" />
								<span>Add Alias</span>
							</button>}
						</div>
					</div>}
				/>}

				{parts.filter(part => part.type != 'apparel').map(part => showPart(part, openPart == part.id))}

			</div>
		</div>;
	}

	componentWillReceiveProps(nextProps: Readonly<BuilderSettingsProps>) {
		maybe(nextProps.selected, sel => {
			if (sel != this.props.selected) this.setState({ openPart: nextProps.selected });
		});
	}

	toggleOpenPart = (key: string) => {
		const openPart = this.state.openPart == key ? false : key;
		this.setState({ openPart });
		if (!openPart) this.props.builder.selectPart(undefined);
	};

	addTextPart = (type: TextPartType = 'text') => (e: Event) => {
		const part = this.props.builder.manager.addTextPart(type);
		this.props.builder.selectPart(part.id);
	};

}

export class BuilderMobileSettings extends Component<BuilderSettingsProps, BuilderMobileSettingsState> {

	constructor(props: BuilderSettingsProps) {
		super(props);
	}

	render({ product, builder, parts, selected, domain }: BuilderSettingsProps, { uploadError, popupColor, popupPart }: BuilderMobileSettingsState) {
		const part = isSome(selected) && parts.find(p => p.id === selected) || null;
		const apparel = findMatch(parts, { type: 'apparel' });
		const { roster, nameNumber } = ensure(product.features);
		return <div className="builder-settings">
			<div className="builder-settings__inner">
				{!selected && <div className="builder-settings__part-list">
					<button
						className="builder-settings__part-switch "
						onClick={() => builder.selectPart('product')}
					>
						<Icon name="apparel" />
						<span>{product.name}</span>
					</button>
					<button class="builder-settings__part-switch builder-settings__part-switch--add-buttons" onClick={() => builder.selectPart('add')}>
						<span><Icon name="plus" />Add...</span>
						<div class="builder-settings__part-switch-icons">
							<Icon name="image-file" />
							<Icon name="text" />
						</div>
					</button>
					{parts.map(p => <button
						className="builder-settings__part-switch"
						onClick={() => builder.selectPart(p.id)}
					>
						{p.upload && <img src={getUploadPreviewURL(p.upload)} />}
						{p.type == 'apparel' && <Icon name="color-palette" />}
						{p.type == 'flag' && <Icon name="flag" />}
						{p.type == 'name' && <Icon name="user" />}
						{p.type == 'number' && <Icon name="one" />}
						{p.type == 'text' && <Icon name="text" />}
						{p.type == 'alias' && <Icon name="alias" />}
						{p.type == 'pw-logo' && (p.domain == 'ts' ? <Icon name="t-s" /> : <Icon name="e-x" />)}
						<span>{getPartTitle(p)}</span>
					</button>)}
				</div>}

				{selected == 'add' && <div className="builder-settings__part-list">
					<LogoUploadButton builder={builder} setError={e => e ? showAlert(e) : undefined} />
					{builder.props.haveUser && <button class={classNames('builder-settings__part-switch', '--browse')} onClick={() => builder.browseArtwork()}>
						<Icon name="folder"/>
						<span>Browse</span>
					</button>}
					<button class={classNames('builder-settings__part-switch', '--add')} onClick={this.addTextPart()}>
						<Icon name="text" />
						<span>Add Text</span>
					</button>
					{roster && nameNumber && <button class={classNames('builder-settings__part-switch', '--add')} onClick={this.addTextPart('name')}>
						<Icon name="user" />
						<span>Add Name</span>
					</button>}
					{roster && nameNumber && <button class={classNames('builder-settings__part-switch', '--add')} onClick={this.addTextPart('number')}>
						<Icon name="one" />
						<span>Add Number</span>
					</button>}
					{roster && nameNumber && <button class={classNames('builder-settings__part-switch', '--add')} onClick={this.addTextPart('alias')}>
						<Icon name="alias" />
						<span>Add Alias</span>
					</button>}
				</div>}
				
				{selected == 'product' && <BuilderProductSettings
					builder={builder}
					product={product}
					apparel={apparel}
				/>}
				{part && <PartConfig
					part={part}
					builder={builder}
					removable={['logo', 'text', 'name', 'number', 'alias'].includes(part.type)}
					mobile={true}
					domain={domain}
				/>}
			</div>
			{isSome(selected) && <div className="builder-settings__mobile-nav">
				<button className="builder-settings__mobile-nav-btn" onClick={() => builder.selectPart(undefined)}><Icon name="arrow-up" /></button>
				<span className="builder-settings__mobile-nav-title">{
					maybe(part, getPartTitle)
					|| (selected == 'product' && product.name)
					|| (selected == 'add' && 'Add image or text')
				}</span>
				<button className="builder-settings__mobile-nav-btn" onClick={() => this.switchPart(-1)}><Icon name="arrow-left" /></button>
				<button className="builder-settings__mobile-nav-btn" onClick={() => this.switchPart(1)}><Icon name="arrow-right" /></button>
			</div>}
		</div>;
	}

	addTextPart = (type: TextPartType = 'text') => (e: Event) => {
		const part = this.props.builder.manager.addTextPart(type);
		this.props.builder.selectPart(part.id);
	};

	switchPart(by: number) {
		const arr = ['product', 'add', ...this.props.parts.map(p => p.id)];
		const cur = arr.indexOf(this.props.selected!);
		const next = (arr.length + cur + by) % arr.length;
		this.props.builder.selectPart(arr[next]);
	}

}
